/**
 * Who a piece of work or a merge request can be handed to - the list behind
 * the people picker, and the pure rules that shape it (issue #67).
 *
 * BEST encodes no review or assignment policy. This list is what GitLab holds,
 * ordered for convenience: the caller first, then the people GitLab's branch
 * protection says can merge, then everyone else. Ordering is the ONLY thing
 * done with `can_merge` here - it never removes anybody from the list, and
 * nothing downstream may treat it as a requirement.
 *
 * The hook is separate from the component because the DIALOGS need the answer
 * too: the start-work dialog pre-fills `me`, and the merge dialog words a line
 * from `merge_rule`. One request per dialog, made the first time the people
 * are actually wanted and kept after that - the list is short, so typing
 * filters it in the browser and never asks again.
 */

import { useEffect, useState } from 'react'
import { api } from '../../api'
import type { MembersPayload, PersonMergeHint } from '../../schema'

/** Where the member list stands, for the picker to draw and the dialog to read. */
export type MembersState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; data: MembersPayload }
  | { status: 'failed'; message: string }

/**
 * Load a project's members, once, the first time `enabled` is true.
 *
 * Kept for the project it was loaded for: a different slug asks again, and a
 * request still in flight for the old one is dropped on arrival rather than
 * shown under the new one. A failure is a state, not a thrown error - the form
 * around the picker must stay usable without the list.
 */
export function useMembers(slug: string, enabled: boolean): MembersState {
  const [loaded, setLoaded] = useState<{ slug: string; state: MembersState }>({
    slug: '',
    state: { status: 'idle' },
  })

  // Once wanted, wanted: a request in flight must not be dropped because the
  // caller stopped asking (a tab switched away from the picker), or the state
  // would sit at 'loading' with nobody left to finish it.
  const [asked, setAsked] = useState('')
  useEffect(() => {
    if (enabled && slug) setAsked(slug)
  }, [enabled, slug])

  // Settled for THIS slug already: neither a re-open nor a tab switch asks again.
  const settled =
    loaded.slug === slug && (loaded.state.status === 'ready' || loaded.state.status === 'failed')
  const wanted = asked === slug

  useEffect(() => {
    if (!wanted || !slug || settled) return
    let live = true
    setLoaded({ slug, state: { status: 'loading' } })
    api
      .members(slug)
      .then((data) => live && setLoaded({ slug, state: { status: 'ready', data } }))
      .catch((failure: Error) =>
        live &&
        setLoaded({
          slug,
          state: { status: 'failed', message: failure.message || 'Could not read the people.' },
        }),
      )
    return () => {
      live = false
    }
  }, [wanted, slug, settled])

  return loaded.slug === slug ? loaded.state : { status: 'idle' }
}

/** What the "can merge" mark says, naming the branch GitLab's rule is about. */
export function canMergeText(branch: string | undefined): string {
  return branch ? `Can merge into ${branch}` : 'Can merge'
}

const byName = (left: PersonMergeHint, right: PersonMergeHint) =>
  left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }) ||
  left.username.localeCompare(right.username)

/**
 * The order people are offered in: the caller, then those marked as able to
 * merge, then everyone else - each group alphabetical by name.
 *
 * Only `can_merge === true` counts as able: false and null (GitLab's rule could
 * not be resolved) both sort with the rest, so nobody is promoted on a guess.
 * Returns a new array; the input is not reordered.
 */
export function orderPeople<T extends PersonMergeHint>(people: T[], meId: number | null): T[] {
  const rank = (person: T) => (person.id === meId ? 0 : person.can_merge === true ? 1 : 2)
  return [...people].sort((left, right) => rank(left) - rank(right) || byName(left, right))
}

/**
 * Whether a person matches what was typed, by name or by username.
 *
 * A leading `@` is ignored because that is how usernames are written in the
 * list and in GitLab, so typing it as seen must still find them.
 */
export function matchesPerson(person: PersonMergeHint, typed: string): boolean {
  const needle = typed.trim().replace(/^@/, '').toLowerCase()
  if (!needle) return true
  return person.name.toLowerCase().includes(needle) || person.username.toLowerCase().includes(needle)
}
